import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import IssueAnalysisPanel from '../../components/quality/IssueAnalysisPanel';
import IssuePatchDraftPanel from '../../components/quality/IssuePatchDraftPanel';

interface IssueRow {
    id: number;
    toolId: number;
    toolCode: string;
    toolName: string;
    fingerprint: string;
    severity: string;
    source: string;
    title: string;
    status: string;
    occurrenceCount: number;
    affectedUserCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
    assignedToUserId?: number;
    assignedToEmail?: string;
    latestEventId?: number;
}

interface IssueOccurrence {
    eventId: number;
    severity: string;
    source: string;
    message: string;
    context?: string;
    userEmail?: string;
    occurredAt: string;
}

interface IssueHistory {
    id: number;
    previousStatus?: string;
    newStatus: string;
    changedByEmail?: string;
    note?: string;
    changedAt: string;
}

interface IssueDetail extends IssueRow {
    createdAt: string;
    updatedAt: string;
    resolvedAt?: string;
    recentOccurrences: IssueOccurrence[];
    statusHistory: IssueHistory[];
}

const STATUS_LABEL: Record<string, string> = {
    New: 'Nuovo',
    Triaged: 'Classificato',
    Investigating: 'In analisi',
    Reopened: 'Riaperto',
    Resolved: 'Risolto',
    Ignored: 'Ignorato',
};

const STATUS_BADGE: Record<string, string> = {
    New: 'bg-danger',
    Triaged: 'bg-primary',
    Investigating: 'bg-warning text-dark',
    Reopened: 'bg-danger',
    Resolved: 'bg-success',
    Ignored: 'bg-secondary',
};

const SEVERITY_BADGE: Record<string, string> = {
    Error: 'bg-danger',
    Warning: 'bg-warning text-dark',
    Info: 'bg-info text-dark',
};

const QualityIssueWorkbenchPage: React.FC = () => {
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState<IssueRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [status, setStatus] = useState(() => searchParams.get('status') || 'active');
    const [severity, setSeverity] = useState(() => searchParams.get('level') || 'all');
    const [source, setSource] = useState(() => searchParams.get('source') || 'all');
    const [toolCode, setToolCode] = useState(() => searchParams.get('toolCode') || '');
    const [sinceHours, setSinceHours] = useState(() => Number(searchParams.get('sinceHours')) || 720);
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [detail, setDetail] = useState<IssueDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailNote, setDetailNote] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await apiService.adminGetDiagnosticIssues<IssueRow>({
                status,
                severity: severity === 'all' ? undefined : severity,
                source: source === 'all' ? undefined : source,
                toolCode: toolCode.trim() || undefined,
                search: search.trim() || undefined,
                sinceHours,
                limit: 500,
            });
            setRows(response);
        } catch (error: unknown) {
            setLoadError(error instanceof Error ? error.message : 'Issue Engine non disponibile');
        } finally {
            setLoading(false);
        }
    }, [search, severity, sinceHours, source, status, toolCode]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        const next = new URLSearchParams();
        if (status !== 'active') next.set('status', status);
        if (severity !== 'all') next.set('level', severity);
        if (source !== 'all') next.set('source', source);
        if (toolCode.trim()) next.set('toolCode', toolCode.trim());
        if (sinceHours !== 720) next.set('sinceHours', String(sinceHours));
        setSearchParams(next, { replace: true });
    }, [setSearchParams, severity, sinceHours, source, status, toolCode]);

    const summary = useMemo(() => ({
        errors: rows.filter(row => row.severity === 'Error').length,
        warnings: rows.filter(row => row.severity === 'Warning').length,
        occurrences: rows.reduce((total, row) => total + row.occurrenceCount, 0),
        reopened: rows.filter(row => row.status === 'Reopened').length,
    }), [rows]);

    const openDetail = async (issueId: number) => {
        setDetailLoading(true);
        setDetail(null);
        setDetailNote('');
        try {
            setDetail(await apiService.adminGetDiagnosticIssue<IssueDetail>(issueId));
        } catch {
            showToast('Errore', 'Dettaglio del problema non disponibile', 'danger');
        } finally {
            setDetailLoading(false);
        }
    };

    const updateStatus = async (issue: IssueRow, nextStatus: string, note?: string) => {
        if (issue.status === nextStatus) return;
        setUpdatingId(issue.id);
        try {
            await apiService.adminUpdateDiagnosticIssueStatus(issue.id, nextStatus, note);
            const updated = { ...issue, status: nextStatus };
            setRows(current => {
                if (status === 'active' && (nextStatus === 'Resolved' || nextStatus === 'Ignored'))
                    return current.filter(row => row.id !== issue.id);
                if (status !== 'all' && status !== 'active' && status !== nextStatus)
                    return current.filter(row => row.id !== issue.id);
                return current.map(row => row.id === issue.id ? updated : row);
            });
            if (detail?.id === issue.id) {
                setDetail(await apiService.adminGetDiagnosticIssue<IssueDetail>(issue.id));
                setDetailNote('');
            }
            showToast('Problema aggiornato', STATUS_LABEL[nextStatus] ?? nextStatus, 'success');
        } catch {
            showToast('Errore', 'Stato del problema non aggiornato', 'danger');
        } finally {
            setUpdatingId(null);
        }
    };

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="mb-1">Problemi persistenti</h5>
                    <div className="small text-body-secondary">
                        Anomalie correlate tramite fingerprint, con lifecycle e storico indipendenti dalla retention.
                    </div>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />{loading ? 'Aggiorno…' : 'Aggiorna'}
                </button>
            </div>

            {loadError && (
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {loadError}. Verifica che la migrazione dell'Issue Engine sia stata applicata.
                </div>
            )}

            <div className="row g-3 mb-3">
                <IssueMetric label="Problemi mostrati" value={rows.length} color="primary" />
                <IssueMetric label="Error" value={summary.errors} color="danger" />
                <IssueMetric label="Warning" value={summary.warnings} color="warning" />
                <IssueMetric label="Occorrenze correlate" value={summary.occurrences} color="info"
                    suffix={summary.reopened > 0 ? ` · ${summary.reopened} riaperti` : undefined} />
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Stato</label>
                            <select className="form-select form-select-sm" value={status}
                                onChange={event => setStatus(event.target.value)}>
                                <option value="active">Attivi</option>
                                <option value="all">Tutti</option>
                                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Severità</label>
                            <select className="form-select form-select-sm" value={severity}
                                onChange={event => setSeverity(event.target.value)}>
                                <option value="all">Tutte</option>
                                <option value="Error">Error</option>
                                <option value="Warning">Warning</option>
                                <option value="Info">Info</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Origine</label>
                            <select className="form-select form-select-sm" value={source}
                                onChange={event => setSource(event.target.value)}>
                                <option value="all">Tutte</option>
                                <option value="Client">Frontend</option>
                                <option value="Server">Backend</option>
                                <option value="Mcp">MCP server</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Periodo</label>
                            <select className="form-select form-select-sm" value={sinceHours}
                                onChange={event => setSinceHours(Number(event.target.value))}>
                                <option value={24}>24 ore</option>
                                <option value={168}>7 giorni</option>
                                <option value={720}>30 giorni</option>
                                <option value={2160}>90 giorni</option>
                                <option value={8760}>1 anno</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-2">
                            <label className="form-label small">Tool code</label>
                            <input className="form-control form-control-sm" value={toolCode}
                                onChange={event => setToolCode(event.target.value)} placeholder="Tutti" />
                        </div>
                        <div className="col-12 col-md">
                            <label className="form-label small">Cerca</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text"><i className="bi bi-search" /></span>
                                <input className="form-control" value={search}
                                    onChange={event => setSearch(event.target.value)}
                                    placeholder="Messaggio, tool o fingerprint" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
                    <strong>{rows.length} problemi</strong>
                    <span className="small text-body-secondary">Ordinati per severità e ultima occorrenza</span>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Severità</th>
                                <th>Problema</th>
                                <th>Tool</th>
                                <th>Stato</th>
                                <th className="text-center">Occorrenze</th>
                                <th className="text-center">Utenti</th>
                                <th>Ultimo evento</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-4">
                                    <span className="spinner-border spinner-border-sm me-2" />Caricamento problemi…
                                </td></tr>
                            )}
                            {!loading && rows.length === 0 && !loadError && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-4">
                                    Nessun problema corrisponde ai filtri.
                                </td></tr>
                            )}
                            {!loading && rows.map(issue => (
                                <tr key={issue.id}>
                                    <td><span className={`badge ${SEVERITY_BADGE[issue.severity] ?? 'bg-secondary'}`}>{issue.severity}</span></td>
                                    <td style={{ minWidth: 300 }}>
                                        <button type="button" className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                            onClick={() => void openDetail(issue.id)}>
                                            {issue.title}
                                        </button>
                                        <div className="small text-body-secondary font-monospace">
                                            #{issue.id} · {issue.fingerprint.slice(0, 12)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="small fw-semibold">{issue.toolName}</div>
                                        <code className="small text-body-secondary">{issue.toolCode}</code>
                                    </td>
                                    <td>
                                        <select className="form-select form-select-sm" value={issue.status}
                                            disabled={updatingId === issue.id} style={{ minWidth: 140 }}
                                            onChange={event => void updateStatus(issue, event.target.value)}>
                                            {Object.entries(STATUS_LABEL).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="text-center fw-semibold">{issue.occurrenceCount}</td>
                                    <td className="text-center">{issue.affectedUserCount || '—'}</td>
                                    <td className="small text-body-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(issue.lastSeenAt).toLocaleString('it-IT')}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => void openDetail(issue.id)}>
                                                <i className="bi bi-eye" />
                                            </button>
                                            <Link className="btn btn-sm btn-outline-secondary" to={`/tools/${issue.toolId}`}>
                                                <i className="bi bi-tools" />
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal show={detail !== null || detailLoading} onHide={() => setDetail(null)} size="xl" centered>
                <Modal.Header closeButton><Modal.Title>Problema diagnostico</Modal.Title></Modal.Header>
                <Modal.Body>
                    {detailLoading && <div className="text-center p-5"><span className="spinner-border" /></div>}
                    {detail && (
                        <>
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                                <span className={`badge ${SEVERITY_BADGE[detail.severity] ?? 'bg-secondary'}`}>{detail.severity}</span>
                                <span className={`badge ${STATUS_BADGE[detail.status] ?? 'bg-secondary'}`}>
                                    {STATUS_LABEL[detail.status] ?? detail.status}
                                </span>
                                <span className="badge bg-light text-dark border">{detail.source}</span>
                                <code className="small ms-auto">{detail.fingerprint}</code>
                            </div>
                            <h5>{detail.title}</h5>
                            <div className="small text-body-secondary mb-3">
                                {detail.toolName} (<code>{detail.toolCode}</code>) · {detail.occurrenceCount} occorrenze ·{' '}
                                prima {new Date(detail.firstSeenAt).toLocaleString('it-IT')} · ultima{' '}
                                {new Date(detail.lastSeenAt).toLocaleString('it-IT')}
                            </div>

                            <div className="card bg-body-tertiary border mb-3">
                                <div className="card-body">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-12 col-md-3">
                                            <label className="form-label small">Cambia stato</label>
                                            <select className="form-select" value={detail.status}
                                                disabled={updatingId === detail.id}
                                                onChange={event => void updateStatus(detail, event.target.value, detailNote)}>
                                                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md">
                                            <label className="form-label small">Nota per la transizione</label>
                                            <input className="form-control" value={detailNote}
                                                onChange={event => setDetailNote(event.target.value)}
                                                placeholder="Motivazione, verifica effettuata o riferimento ticket" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <IssueAnalysisPanel issueId={detail.id} />
                            <IssuePatchDraftPanel issueId={detail.id} />


                            <div className="row g-3">
                                <div className="col-12 col-xl-8">
                                    <h6>Occorrenze recenti</h6>
                                    <div className="table-responsive border rounded" style={{ maxHeight: 360 }}>
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead className="table-light sticky-top">
                                                <tr><th>Quando</th><th>Context</th><th>Utente</th><th /></tr>
                                            </thead>
                                            <tbody>
                                                {detail.recentOccurrences.map(occurrence => (
                                                    <tr key={occurrence.eventId}>
                                                        <td className="small" style={{ whiteSpace: 'nowrap' }}>
                                                            {new Date(occurrence.occurredAt).toLocaleString('it-IT')}
                                                        </td>
                                                        <td className="small"><code>{occurrence.context || '—'}</code></td>
                                                        <td className="small">{occurrence.userEmail || '—'}</td>
                                                        <td>
                                                            <Link className="btn btn-sm btn-link"
                                                                to={`/quality/events?toolCode=${encodeURIComponent(detail.toolCode)}&level=${encodeURIComponent(detail.severity)}`}>
                                                                Eventi
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-4">
                                    <h6>Storico stato</h6>
                                    <div className="d-flex flex-column gap-2">
                                        {detail.statusHistory.map(item => (
                                            <div key={item.id} className="border rounded p-2 small">
                                                <div className="d-flex justify-content-between gap-2">
                                                    <strong>{STATUS_LABEL[item.newStatus] ?? item.newStatus}</strong>
                                                    <span className="text-body-secondary">
                                                        {new Date(item.changedAt).toLocaleString('it-IT')}
                                                    </span>
                                                </div>
                                                <div className="text-body-secondary">{item.changedByEmail || 'Sistema'}</div>
                                                {item.note && <div className="mt-1">{item.note}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {detail && <Link className="btn btn-outline-secondary" to={`/tools/${detail.toolId}`}>Configura tool</Link>}
                    <Button variant="secondary" onClick={() => setDetail(null)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const IssueMetric: React.FC<{ label: string; value: number; color: string; suffix?: string }> =
    ({ label, value, color, suffix }) => (
        <div className="col-6 col-xl-3">
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <div className="small text-body-secondary">{label}</div>
                    <div className={`fs-4 fw-bold text-${color}`}>{value}</div>
                    {suffix && <div className="small text-body-secondary">{suffix}</div>}
                </div>
            </div>
        </div>
    );

export default QualityIssueWorkbenchPage;
